import { useEffect } from 'react'
import { TextLibrary } from '../../consts/defaultConfigValues/defaultConstants'
import { setPlaceOrderFlow } from '../../redux/slices/cart'
import type { PlaceOrderFlowEntry } from '../../interfaces'
import { setCtaLabel, setGuidedReturn } from '../../redux/slices/checkout'
import { selectFirstHeldRule } from '../../utils/checkoutRuleSelection'
import { prepareBeforeOrderQueue } from '../../utils/workflowStepDuplicates'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import usePushView from '../usePushView'
import useStartGuidedWalk, {
  type UseStartGuidedWalkArgs,
} from './useStartGuidedWalk'

/**
 * Keeps the summary CTA's caption in step with what the shopper still owes.
 *
 * *Continuar* while an editable checkout rule is unchosen, *Realizar pedido*
 * once they are all settled. `selectFirstHeldRule` decides; this only writes
 * the answer somewhere the config language can read it.
 *
 * **Mounted by `templates/Summary.tsx`.** It reads and dispatches, and starts
 * no fetch — so unlike `useCheckoutRuleAutoSettle` beside it, it carries no
 * one-live-instance constraint and nothing here bears on that warning.
 *
 * ⚠️ **It writes a token, not resolved copy.** See `ctaLabel` in
 * `redux/slices/checkout/types.ts`: a `{{texts.*}}` token is baked at boot, so
 * the caption cannot be a text token and cannot be an `||` either. Both
 * members below are tokens, and `processData` resolves them a second time.
 *
 * The selector's result is narrowed to a boolean before it reaches
 * `useAppSelector`, so the component re-renders when the *answer* flips rather
 * than on every store action that rebuilds the held rule's object.
 */
export const useSummaryCheckoutCta = (): void => {
  const dispatch = useAppDispatch()
  const held = useAppSelector(
    (state) => selectFirstHeldRule(state) !== undefined,
  )
  const current = useAppSelector((state) => state.checkoutSlice.ctaLabel)

  useEffect(() => {
    const next = held
      ? TextLibrary.SUMMARY_CONTINUE_TO_SELECTION
      : TextLibrary.SUMMARY_CONTINUE_BUTTON

    /*
     * Skips a dispatch that would change nothing — and that is all it does.
     *
     * ⚠️ **It is not what stops a render loop.** An earlier version of this
     * comment said so, and the mutant that deletes this line was measured:
     * every test still passes. Two things independently prevent the loop this
     * effect looks like it should have. Immer returns the original state when
     * a reducer assigns an equal value, so `checkoutSlice` keeps its
     * reference; and `current` is selected as a primitive, so `useAppSelector`
     * re-renders only when the caption itself moves. Without this line the
     * cost is exactly one no-op dispatch per swap.
     *
     * Kept because the effect does read the field it writes, and the next
     * editor should meet that fact here rather than discover it. Do not
     * re-justify it as loop prevention.
     */
    if (next === current) return

    dispatch(setCtaLabel(next))
  }, [dispatch, held, current])
}

export default useSummaryCheckoutCta

/** What the summary CTA's authored `args` carry. */
export interface SummaryCtaArgs {
  actions?: PlaceOrderFlowEntry[]
  pushAfter?: string
}

/**
 * What the summary CTA does when the shopper taps it.
 *
 * Bound in `containers/EventManager.tsx` to
 * `TemplateActionNames.SUMMARY_CONTINUE_OR_PLACE_ORDER`. One handler with two
 * branches, because `processActions.createFunction` reduces over
 * `cinnamonActions` synchronously and fires every entry — a branch cannot be
 * expressed as two authored actions.
 *
 * ⚠️ **It must never call `usePlaceOrderFlow()`.** That hook owns an effect
 * that runs `hooksMap[actions[0]]` and an unmount cleanup that clears the
 * queue, and `EventManager` already mounts exactly one instance. A second
 * would double-run every queued action and add a second cleanup.
 * `dispatch(setPlaceOrderFlow(...))` is all `placeOrderFlow()` does, so that
 * is what this dispatches.
 *
 * The store is read fresh rather than from a render closure, for the reason
 * `confirm()` and `select` in `useCheckoutRuleSelection` both give:
 * `EventManager` registers its handlers in an effect keyed on the redux
 * state, so a value captured at render can be a navigation behind by the time
 * the shopper taps.
 *
 * **Also returns `startGuidedWalk` (COM-1501)**, the other way into the same
 * walk, so the two producers of `guidedReturnPath` come from one hook. Its
 * logic lives in `useStartGuidedWalk`; `walkWrites` are the picker screens'
 * `select`s, which `EventManager` passes in so the walk mounts none of its
 * own.
 */
export const useSummaryCheckoutActions = (
  walkWrites: UseStartGuidedWalkArgs = {},
) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const pushView = usePushView()
  const { startGuidedWalk } = useStartGuidedWalk(walkWrites)

  const continueOrPlaceOrder = (args: SummaryCtaArgs = {}) => {
    const state = store.getState()
    const next = selectFirstHeldRule(state)

    if (!next) {
      // `commerce-begin_checkout` is fired by `useLoadDialogValidations` when
      // the `CONFIRM_ORDER` dialog actually opens — both this summary CTA
      // and the default cart footer's `placeOrderFlow` reach that opener, so
      // one site counts both entry points once, and an interrupted
      // before-order queue (a validator that aborts) is not counted as a
      // checkout.
      //
      // Prepared as `placeOrderFlow()` prepares it: this is the other
      // producer of the queue, so a step after the order is dropped and a
      // step listed twice runs once (COM-1296).
      const queue = prepareBeforeOrderQueue(args.actions ?? [])
      dispatch(
        setPlaceOrderFlow({
          actions: queue,
          pushAfter: args.pushAfter,
        }),
      )
      return
    }

    /*
     * The return address, written immediately before the push and nowhere
     * else. Built from the store rather than captured from
     * `window.location.pathname`, which `routes/index.tsx`'s `basename` would
     * make `navigate()` prepend a second time.
     *
     * It needs no origin guard of its own: `selectFirstHeldRule` already
     * refused every path built from this same `storeName`, so reaching this
     * line proves the slug does not escape the origin.
     */
    const { storeName, sessionId } = state.defaultSlice
    dispatch(setGuidedReturn(`/${storeName}/${sessionId}/summary`))
    // `state` here is react-router's NavigateOptions. The inner `state` field
    // is the history-state payload the picker templates read via `useLocation`.
    pushView({
      path: next.path,
      state: { state: { entryPoint: 'guided_walk' } },
    })
  }

  return { continueOrPlaceOrder, startGuidedWalk }
}
